// Global
import { useEffect } from 'react';
import {
  Field,
  Item,
  LinkField,
  ImageField,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';

// Marquee helpers
import { useMarqueeCarousel } from '../useMarqueeCarousel';
import { CarouselMarqueeProps } from '../marqueeTypes';
import { DotButton, useDotButton } from '../MarqueeDotButton';
import { PrevButton, NextButton, usePrevNextButtons } from '../MarqueeArrowButton';

// Other Helpers
import RichTextA11yWrapper from 'helpers/RichTextA11yWrapper/RichTextA11yWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import { useLoginContext } from 'helpers/Providers/LoginContextProvider';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { Bok } from 'lib/templates/Foundation.BOKF.model';

const HeroMarquee = (props: CarouselMarqueeProps): JSX.Element => {
  const { currentScreenWidth } = useCurrentScreenType();
  const {
    marquees,
    width,
    emblaRef,
    emblaApi,
    enableCarousel,
    loginMarqueeRef,
    onNavButtonClick,
    toggleAutoplay,
  } = useMarqueeCarousel(props);

  const { isMarqueeLoginEnabled, setMarqueeLoginEnabled } = useLoginContext();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi, onNavButtonClick);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi, onNavButtonClick);

  // Login Effects
  useEffect(() => {
    setMarqueeLoginEnabled(props?.fields?.EnableLoginBox?.value as boolean);
  }, [setMarqueeLoginEnabled, props?.fields?.EnableLoginBox?.value]);

  useEffect(() => {
    if (isMarqueeLoginEnabled) {
      const loginElement = document.querySelector('[data-component="login"]');
      if (loginElement && loginMarqueeRef.current) {
        loginMarqueeRef.current.appendChild(loginElement);
      }
    }
  }, [loginMarqueeRef, isMarqueeLoginEnabled]);

  const { fields = undefined } = props;
  if (!fields || marquees?.length === 0) {
    return <></>;
  }

  return (
    <section className={`component carousel-marquee ${width}`} data-component="marquee">
      <div className="carousel-marquee-viewport group" ref={emblaRef}>
        <div className="carousel-marquee-container">
          {marquees?.map((item) => {
            return (
              <div
                className="carousel-marquee-slide max-h-[424px] lg:max-h-[500px]"
                key={item.id}
                data-id={item.id}
              >
                <div className="flex justify-start items-center absolute top-0 left-0 bottom-0 w-full hero-experience">
                  <div className="container">
                    <div className="my-auto md:max-w-[540px] sm:mx-4 md:ml-8 lg:ml-10">
                      <RichTextA11yWrapper
                        field={item?.fields?.Title as RichTextField}
                        tag={
                          (((item?.fields?.TitleTag as Item)?.fields?.Tag as Field)
                            ?.value as string) || 'h1'
                        }
                        className={`pb-2 ${
                          ((item?.fields?.LightModeToggle as Field)?.value as boolean)
                            ? 'text-light-fill-inverse'
                            : 'text-light-fill-primary'
                        }`}
                      ></RichTextA11yWrapper>

                      <div
                        className={`w-[48px] h-[4px] ${
                          ((item?.fields?.TitleBorderStyle as Item)?.fields?.Value as Field)?.value
                        }`}
                      ></div>

                      <RichTextA11yWrapper
                        field={item?.fields?.RichText as RichTextField}
                        className={`py-6 rtaw ${
                          ((item?.fields?.LightModeToggle as Field)?.value as boolean)
                            ? 'text-light-fill-inverse'
                            : 'text-light-fill-primary'
                        }`}
                      ></RichTextA11yWrapper>

                      <div className="grid grid-cols-1 md:flex gap-4">
                        {item?.fields?.ButtonOne && (
                          <LinkWrapper
                            field={item?.fields?.ButtonOne as LinkField}
                            className={
                              (item?.fields?.ButtonOneStyle as Bok.BaseTypes.Selector)?.fields
                                ?.Value?.value
                            }
                          />
                        )}
                        {item?.fields?.ButtonTwo && (
                          <LinkWrapper
                            field={item?.fields?.ButtonTwo as LinkField}
                            className={
                              (item?.fields?.ButtonTwoStyle as Bok.BaseTypes.Selector)?.fields
                                ?.Value?.value
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {currentScreenWidth >= getBreakpoint('desktop') && (
                  <ImageWrapper
                    field={item?.fields?.DesktopImage as ImageField}
                    className="min-h-[500px] w-full object-cover object-left"
                  />
                )}
                {currentScreenWidth >= getBreakpoint('tablet') &&
                  currentScreenWidth < getBreakpoint('desktop') && (
                    <ImageWrapper
                      field={item?.fields?.TabletImage as ImageField}
                      className="min-h-[424px] w-full object-cover object-center"
                    />
                  )}
                {currentScreenWidth < getBreakpoint('tablet') && (
                  <ImageWrapper
                    field={item?.fields?.MobileImage as ImageField}
                    className="min-h-[424px] w-full object-cover"
                  />
                )}
              </div>
            );
          })}
        </div>
        <div
          className={`previous-button-container ${
            !enableCarousel ? 'hidden' : ''
          } opacity-0 group-hover:opacity-100`}
        >
          <PrevButton
            className="previous-button"
            onClick={onPrevButtonClick}
            disabled={prevBtnDisabled}
          />
        </div>
        <div
          className={`next-button-container ${
            !enableCarousel ? 'hidden' : ''
          } opacity-0 group-hover:opacity-100`}
        >
          <NextButton
            className="next-button"
            onClick={onNextButtonClick}
            disabled={nextBtnDisabled}
          />
        </div>
        {isMarqueeLoginEnabled && (
          <div className="carousel-marquee-login" ref={loginMarqueeRef}></div>
        )}
      </div>
      <div
        className={`carousel-marquee-controls ${
          !enableCarousel ? 'hidden' : ''
        } relative bg-transparent`}
      >
        <div className="flex flex-row h-[44px] justify-center items-center px-4 md:px-6 stroke-light-stroke-primary bg-light-background-neutral-variant border absolute left-0">
          <label className="carousel-toggle inline-flex items-center mr-2 w-auto">
            <input type="checkbox" onClick={toggleAutoplay} aria-label="Pause Carousel" />
            <span className="carousel-slider w-[46px] bg-light-background-neutral-highest"></span>
            <div className="p2 ps-2 ms-12 text-light-fill-primary-variant">Pause Carousel</div>
          </label>
        </div>
        <div className="carousel-marquee-dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={marquees?.[index]?.id ?? index}
              data-id={marquees?.[index]?.id ?? index}
              onClick={() => onDotButtonClick(index)}
              className={'carousel-marquee-dot'.concat(
                index === selectedIndex ? ' carousel-marquee-dot--selected' : ''
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroMarquee;
